import { Button, Flex, Group, Paper, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconDownload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { DeleteButton } from '@/core/components';

import { FilePreviewButton, FilePreviewModal, formatDateTime, useAppNotification } from '@feature/utils';

import {
  useTravelRequestDeleteFile,
  useTravelRequestDownloadFile,
  useTravelRequestFileBlob,
} from '../../model/TravelRequestQuery';
import { TravelActivityType, TravelRequestFile, travelFileKindLabel } from '../../model/TravelRequestTypes';

interface TravelRequestFileRowProps {
  requestId: number;
  requestFile: TravelRequestFile;
  activityType: TravelActivityType;
  canDelete: boolean;
  onDeleted?: () => void;
}

export function TravelRequestFileRow({
  requestId,
  requestFile,
  activityType,
  canDelete,
  onDeleted,
}: TravelRequestFileRowProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const deleteFile = useTravelRequestDeleteFile(requestId);
  const downloadFile = useTravelRequestDownloadFile(requestId);
  const fileBlob = useTravelRequestFileBlob(requestId);
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false);

  // The refetch drops this row before per-call callbacks run, so the toasts hang off the promise, which resolves either way to close the confirm.
  const onDelete = () =>
    deleteFile.mutateAsync(requestFile.id).then(
      () => {
        notification.success();
        onDeleted?.();
      },
      (error: Error) => notification.error(error),
    );

  const onDownload = () =>
    downloadFile.mutate(
      { fileId: requestFile.id, fallbackFilename: requestFile.file.title },
      { onError: notification.error },
    );

  return (
    <Paper p="sm">
      <Flex
        justify="space-between"
        align="flex-start"
      >
        <Stack
          gap={4}
          flex={1}
        >
          <Text
            size="sm"
            fw="500"
          >
            {requestFile.file.title}
          </Text>
          <Text
            size="xs"
            c="dimmed"
          >
            {travelFileKindLabel(t, requestFile.kind, activityType)}
          </Text>
          <Text
            size="xs"
            c="dimmed"
          >
            {t('travel.requests.view.uploadedBy')}: {requestFile.uploadedBy.name}
          </Text>
          <Text
            size="xs"
            c="dimmed"
          >
            {t('travel.requests.view.uploadedAt')}: {formatDateTime(requestFile.uploadedAt)}
          </Text>
        </Stack>
        <Group gap="xs">
          <FilePreviewButton
            contentType={requestFile.file.contentType}
            onClick={openPreview}
          />
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconDownload size={14} />}
            onClick={onDownload}
            loading={downloadFile.isPending}
          >
            {t('travel.requests.view.downloadFile')}
          </Button>
          {canDelete && (
            <DeleteButton
              size="xs"
              confirmTitle={t('travel.requests.view.confirmDeleteFile.title')}
              confirmContent={t('travel.requests.view.confirmDeleteFile.content')}
              onDelete={onDelete}
              isDeleting={deleteFile.isPending}
            />
          )}
        </Group>
      </Flex>
      {previewOpened && (
        <FilePreviewModal
          title={requestFile.file.title}
          contentType={requestFile.file.contentType}
          fetchBlob={() => fileBlob.mutateAsync(requestFile.id)}
          onClose={closePreview}
          onDownload={onDownload}
          downloading={downloadFile.isPending}
        />
      )}
    </Paper>
  );
}
